import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import * as Sentry from '@sentry/react'
import type { DesignState } from './types'
import type { UpdateDesignRequest } from '../infrastructure/api/designApi'
import { designApi } from '../infrastructure/api/designApi'
import { createSaveDesignUseCase } from '../application/design/saveDesign'
import { createCreateDesignUseCase } from '../application/design/createDesign'
import { createLoadDesignUseCase } from '../application/design/loadDesign'
import {
  pushToDataLayer,
  trackDesignEvent,
  trackDesignLoad,
  trackError,
  trackSaveEvent,
} from '../infrastructure/analytics/gtm'

/**
 * Design Store
 * Manages design metadata, loading, and saving state
 */

type DesignActions = {
  // Actions
  createDesign: (
    cartId: string,
    productId: number,
    variationId?: number,
    templateName?: string,
    language?: string,
  ) => Promise<void>
  loadDesign: (designId: string) => Promise<void>
  saveDesign: (elements: UpdateDesignRequest['elements']) => Promise<void>

  // Zone overrides
  updateZoneOverrides: (zones: DesignState['zoneOverrides']) => void
  resetZoneOverrides: () => void

  // Save state
  setSaveState: (
    state: Partial<Pick<DesignState, 'isSaving' | 'lastSaved' | 'saveError' | 'retryAttempt'>>,
  ) => void

  // Add to cart state
  setAddingToCart: (isAddingToCart: boolean) => void

  // Reset
  reset: () => void
}

const initialState: DesignState = {
  currentDesign: null,
  isLoading: false,
  isSaving: false,
  isAddingToCart: false,
  lastSaved: null,
  saveError: null,
  retryAttempt: 0,
  zoneOverrides: null,
}

export const useDesignStore = create<DesignState & DesignActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      createDesign: async (cartId, productId, variationId, templateName, language) => {
        set({ isLoading: true })

        try {
          const useCase = createCreateDesignUseCase(designApi)
          const result = await useCase.execute({
            cartId,
            productId: productId.toString(),
            variationId: variationId?.toString(),
            templateName,
            language: language as 'nl' | 'fr' | 'en' | 'de' | undefined,
          })

          if (result.success && result.design) {
            set({
              // eslint-disable-next-line
              currentDesign: result.design as any, // Type conversion needed for API response
              isLoading: false,
            })

            // Track successful design creation
            trackDesignEvent('create_success', 'design', result.design.designId)
            pushToDataLayer({
              design_id: result.design.designId,
              product_id: productId.toString(),
              variation_id: variationId?.toString(),
              template_name: templateName,
              language,
              cart_id: cartId,
            })
          } else {
            set({
              isLoading: false,
              saveError: result.error || 'Failed to create design',
            })
            trackError('create_failed', result.error || 'Unknown error')
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create design'
          set({
            isLoading: false,
            saveError: errorMessage,
          })
          Sentry.captureException(error, {
            tags: { component: 'designStore', operation: 'createDesign' },
            extra: { cartId, productId, variationId },
          })
          trackError('create_exception', errorMessage)
        }
      },

      loadDesign: async (designId) => {
        set({ isLoading: true })

        try {
          const useCase = createLoadDesignUseCase(designApi)
          const result = await useCase.execute({ designId })

          if (result.success && result.design) {
            set({
              // eslint-disable-next-line
              currentDesign: result.design as any, // Type conversion needed for API response
              isLoading: false,
            })

            // Track successful design load
            const elementCount = result.elements ? Object.keys(result.elements).length : 0
            trackDesignLoad(designId, result.design.productId?.toString(), elementCount)

            // If elements were loaded, set them in canvas store
            if (result.elements) {
              const { useCanvasStore } = await import('./canvasStore')
              useCanvasStore.getState().setElements(result.elements)
            }
          } else {
            set({
              isLoading: false,
              saveError: result.error || 'Failed to load design',
            })
            trackError('load_failed', result.error || 'Unknown error', designId)
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to load design'
          set({
            isLoading: false,
            saveError: errorMessage,
          })
          Sentry.captureException(error, {
            tags: { component: 'designStore', operation: 'loadDesign' },
            extra: { designId },
          })
          trackError('load_exception', errorMessage, designId)
        }
      },

      saveDesign: async (elements) => {
        const { currentDesign, isSaving } = get()
        if (!currentDesign || isSaving) return

        // Abort controller for cancelling saves
        const abortController = new AbortController()

        set({ isSaving: true, saveError: null, retryAttempt: 0 })
        trackSaveEvent('start', currentDesign.designId, Object.keys(elements).length)

        try {
          const saveDesign = createSaveDesignUseCase()

          // Transform elements to match backend expectations
          const transformedElements: any[] = []

          for (const [, element] of Object.entries(elements)) {
            const transformed = JSON.parse(JSON.stringify(element)) // Deep clone

            // Remove deprecated fields from asset elements
            if (transformed.type === 'asset') {
              delete transformed.folderName
              delete transformed.fileName
              // Ensure assetType is present (required by backend)
              if (!transformed.assetType && transformed.assetId) {
                Sentry.captureMessage('Asset element missing assetType', {
                  level: 'warning',
                  tags: { component: 'designStore', operation: 'saveDesign' },
                  extra: { elementId: transformed.id, assetId: transformed.assetId },
                })
              }
            }

            transformedElements.push(transformed)
          }

          const saveRequest: UpdateDesignRequest = {
            elements: transformedElements,
          }

          const result = await saveDesign.execute({
            designId: currentDesign.designId,
            design: saveRequest,
            signal: abortController.signal,
          })

          if (result.success) {
            set({ isSaving: false, lastSaved: new Date(), saveError: null, retryAttempt: 0 })
            trackSaveEvent('success', currentDesign.designId, Object.keys(elements).length)
          } else {
            set({ isSaving: false, saveError: result.error })
            trackSaveEvent('error', currentDesign.designId, Object.keys(elements).length)
            trackError('save_failed', result.error || 'Unknown error', currentDesign.designId)
          }
        } catch (error) {
          if (error instanceof Error && error.name !== 'AbortError') {
            set({ isSaving: false, saveError: error.message })
            trackSaveEvent('error', currentDesign.designId, Object.keys(elements).length)
            Sentry.captureException(error, {
              tags: { component: 'designStore', operation: 'saveDesign' },
              extra: {
                designId: currentDesign.designId,
                elementCount: Object.keys(elements).length,
              },
            })
            trackError('save_exception', error.message, currentDesign.designId)
          }
        }
      },

      updateZoneOverrides: (zones) => {
        set({ zoneOverrides: zones })
      },

      resetZoneOverrides: () => {
        const { currentDesign } = get()
        if (!currentDesign) return

        set({
          zoneOverrides: {
            export: { ...currentDesign.zones.export },
            design: { ...currentDesign.zones.design },
          },
        })
      },

      setSaveState: (state) => {
        set(state)
      },

      setAddingToCart: (isAddingToCart) => {
        set({ isAddingToCart })
      },

      reset: () => {
        set(initialState)
      },
    }),
    {
      name: 'design-store',
    },
  ),
)
